var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as signalR from '@microsoft/signalr';
import { generateUUID, trimSlash } from '@utils';
// ============================================================================
// Utilities
// ============================================================================
function log(level, ...args) {
    const timestamp = `[MessageBus : ${new Date().toISOString()}]`;
    if (level === 'error') {
        console.error(timestamp, ...args);
    }
    else if (level === 'warn') {
        console.warn(timestamp, ...args);
    }
    else {
        console.info(timestamp, ...args);
    }
}
function calculateBackoffTimeout(errorCount, baseTimeout = 1000) {
    if (errorCount === 0)
        return baseTimeout;
    if (errorCount === 1)
        return baseTimeout * 2;
    // Exponential backoff with jitter for errors >= 2
    const jitter = Math.random();
    return jitter * Math.pow(2, errorCount) * baseTimeout;
}
// ============================================================================
// MessageBus Implementation
// ============================================================================
const MessageBus = {
    // State
    subscriptions: {},
    subscriptionFunctionMap: {},
    initialized: false,
    startedInitialization: false,
    token: '',
    initQueue: [],
    globalHub: null,
    /**
     * Initialize the MessageBus connection
     * @param opts Configuration options with url and apiKey
     * @returns Promise with operation result
     */
    init: function (opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                try {
                    if (!signalR) {
                        resolve({ success: false, error: 'SignalR client library not loaded' });
                        return;
                    }
                    if (!(opts === null || opts === void 0 ? void 0 : opts.url)) {
                        resolve({ success: false, error: 'No url specified' });
                        return;
                    }
                    MessageBus.token = opts.apiKey || '';
                    // Already initialized
                    if (MessageBus.initialized) {
                        resolve({ success: true });
                        return;
                    }
                    // Create hub connection if not exists
                    if (!MessageBus.globalHub) {
                        MessageBus.globalHub = new signalR.HubConnectionBuilder()
                            .withUrl(trimSlash(opts.url) + '/globalhub', {
                            skipNegotiation: true,
                            transport: signalR.HttpTransportType.WebSockets
                        })
                            .configureLogging(signalR.LogLevel.Debug)
                            .build();
                    }
                    const connection = MessageBus.globalHub;
                    MessageBus.initQueue.push(resolve);
                    // Setup connection only once
                    if (!MessageBus.startedInitialization) {
                        connection.serverTimeoutInMilliseconds = 30000;
                        // Handle incoming messages
                        connection.on('broadcast', (channel, message) => {
                            const handlers = MessageBus.subscriptions[channel];
                            if (handlers) {
                                handlers.forEach(handler => {
                                    try {
                                        handler(message, channel);
                                    }
                                    catch (err) {
                                        log('error', `Error in message handler for channel ${channel}:`, err);
                                    }
                                });
                            }
                        });
                        // Handle disconnects with auto-reconnect
                        connection.onclose(() => __awaiter(this, void 0, void 0, function* () {
                            log('warn', 'Connection closed, attempting to reconnect...');
                            MessageBus.initialized = false;
                            yield MessageBus.reconnect(connection, 0);
                        }));
                        log('info', 'Starting connection to global hub');
                        connection.start()
                            .then(() => {
                            log('info', 'Connected to global hub');
                            MessageBus.initialized = true;
                            MessageBus.initQueue.forEach(callback => callback({ success: true }));
                            MessageBus.initQueue = [];
                        })
                            .catch((err) => {
                            log('error', 'Failed to start connection:', err);
                            MessageBus.initQueue.forEach(callback => callback({ success: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Connection failed' }));
                            MessageBus.initQueue = [];
                        });
                        MessageBus.startedInitialization = true;
                    }
                }
                catch (err) {
                    log('error', 'Error during initialization:', err);
                    resolve({ success: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Unknown error' });
                }
            });
        });
    },
    /**
     * Reconnect to the hub with exponential backoff
     * @param connection SignalR hub connection
     * @param errorCount Number of consecutive errors
     */
    reconnect: function (connection, errorCount) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield connection.start();
                log('info', 'Reconnected successfully');
                MessageBus.initialized = true;
            }
            catch (err) {
                const timeout = calculateBackoffTimeout(errorCount);
                log('error', `Reconnection attempt ${errorCount + 1} failed, retrying in ${timeout}ms:`, err);
                setTimeout(() => MessageBus.reconnect(connection, errorCount + 1), timeout);
            }
        });
    },
    /**
     * Subscribe to a channel
     * @param channel Channel name to subscribe to
     * @param handler Callback function for messages
     * @returns UUID for this specific subscription (for targeted unsubscribe)
     */
    subscribe: function (channel, handler) {
        try {
            if (!MessageBus.globalHub) {
                return { success: false, error: 'MessageBus not initialized' };
            }
            const isNewChannel = !MessageBus.subscriptions[channel];
            if (isNewChannel) {
                MessageBus.subscriptions[channel] = [];
                log('info', `Subscribing to channel: ${channel}`);
                MessageBus.globalHub
                    .invoke('subscribe', MessageBus.token, channel)
                    .catch((err) => log('error', `Failed to subscribe to channel ${channel}:`, err));
            }
            MessageBus.subscriptions[channel].push(handler);
            const uuid = generateUUID();
            MessageBus.subscriptionFunctionMap[uuid] = { handler, channel };
            return { success: true, data: uuid };
        }
        catch (err) {
            log('error', 'Error in subscribe:', err);
            return { success: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Subscribe failed' };
        }
    },
    /**
     * Publish a message to a channel
     * @param channel Channel name to publish to
     * @param message Message to send
     * @returns Operation result
     */
    publish: function (channel, message) {
        try {
            if (!MessageBus.globalHub) {
                return { success: false, error: 'MessageBus not initialized' };
            }
            log('info', `Publishing to channel: ${channel}`);
            MessageBus.globalHub
                .invoke('broadcast', MessageBus.token, channel, message)
                .catch((err) => log('error', `Failed to publish to channel ${channel}:`, err));
            return { success: true };
        }
        catch (err) {
            log('error', 'Error in publish:', err);
            return { success: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Publish failed' };
        }
    },
    /**
     * Unsubscribe from a channel
     * @param channelOrUuid Either a channel name (unsubscribes all handlers) or a UUID (unsubscribes specific handler)
     * @returns Operation result
     */
    unsubscribe: function (channelOrUuid) {
        try {
            if (!MessageBus.globalHub) {
                return { success: false, error: 'MessageBus not initialized' };
            }
            // Check if it's a UUID for a specific subscription
            const subscriptionDetails = MessageBus.subscriptionFunctionMap[channelOrUuid];
            if (subscriptionDetails) {
                const { handler, channel } = subscriptionDetails;
                const handlers = MessageBus.subscriptions[channel];
                if (handlers) {
                    const handlerIndex = handlers.indexOf(handler);
                    if (handlerIndex >= 0) {
                        handlers.splice(handlerIndex, 1);
                        delete MessageBus.subscriptionFunctionMap[channelOrUuid];
                        // If no more handlers for this channel, unsubscribe from hub
                        if (handlers.length === 0) {
                            delete MessageBus.subscriptions[channel];
                            log('info', `Unsubscribing from channel: ${channel}`);
                            MessageBus.globalHub
                                .invoke('unsubscribe', MessageBus.token, channel)
                                .catch((err) => log('error', `Failed to unsubscribe from channel ${channel}:`, err));
                        }
                        return { success: true };
                    }
                }
            }
            // Otherwise treat as channel name and remove all subscriptions
            const channel = channelOrUuid;
            if (MessageBus.subscriptions[channel]) {
                log('info', `Unsubscribing from channel: ${channel}`);
                MessageBus.globalHub
                    .invoke('unsubscribe', MessageBus.token, channel)
                    .catch((err) => log('error', `Failed to unsubscribe from channel ${channel}:`, err));
                delete MessageBus.subscriptions[channel];
                // Clean up function map
                Object.keys(MessageBus.subscriptionFunctionMap).forEach(uuid => {
                    if (MessageBus.subscriptionFunctionMap[uuid].channel === channel) {
                        delete MessageBus.subscriptionFunctionMap[uuid];
                    }
                });
                return { success: true };
            }
            return { success: false, error: 'No subscription found' };
        }
        catch (err) {
            log('error', 'Error in unsubscribe:', err);
            return { success: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Unsubscribe failed' };
        }
    }
};
// ============================================================================
// Wrapper Functions - Exported for convenience
// ============================================================================
/**
 * Subscribe to a channel with automatic initialization
 * Handles all error logging internally
 *
 * @param context Lucy context with apiKey and lucyUrl
 * @param channel Channel name to subscribe to
 * @param callback Handler function for incoming messages
 * @returns Subscription UUID (for unsubscribing) or null if failed
 */
export function subscribeToChannel(context, channel, callback) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Initialize MessageBus
            const initResult = yield MessageBus.init({
                apiKey: context.apiKey,
                url: context.lucyUrl
            });
            if (!initResult.success) {
                log('error', 'Failed to initialize MessageBus for subscription:', initResult.error);
                return null;
            }
            // Subscribe to channel
            const subResult = MessageBus.subscribe(channel, callback);
            if (!subResult.success) {
                log('error', 'Failed to subscribe to channel:', subResult.error);
                return null;
            }
            return subResult.data || null;
        }
        catch (err) {
            log('error', 'Error in subscribeToChannel:', err);
            return null;
        }
    });
}
/**
 * Unsubscribe from a channel
 * Handles all error logging internally
 *
 * @param channelOrUuid Either a channel name or subscription UUID
 * @returns True if successful, false otherwise
 */
export function unsubscribeFromChannel(channelOrUuid) {
    try {
        const result = MessageBus.unsubscribe(channelOrUuid);
        if (!result.success) {
            log('error', 'Failed to unsubscribe:', result.error);
            return false;
        }
        return true;
    }
    catch (err) {
        log('error', 'Error in unsubscribeFromChannel:', err);
        return false;
    }
}
/**
 * Publish a message to a channel with automatic initialization
 * Handles all error logging internally
 *
 * @param context Lucy context with apiKey and lucyUrl
 * @param channel Channel name to publish to
 * @param message Message to send
 * @returns True if successful, false otherwise
 */
export function publishToChannel(context, channel, message) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Initialize MessageBus
            const initResult = yield MessageBus.init({
                apiKey: context.apiKey,
                url: context.lucyUrl
            });
            if (!initResult.success) {
                log('error', 'Failed to initialize MessageBus for publishing:', initResult.error);
                return false;
            }
            // Publish message
            const pubResult = MessageBus.publish(channel, message);
            if (!pubResult.success) {
                log('error', 'Failed to publish message:', pubResult.error);
                return false;
            }
            return true;
        }
        catch (err) {
            log('error', 'Error in publishToChannel:', err);
            return false;
        }
    });
}
export default MessageBus;
